import React, { useMemo } from 'react';
import _reverse from 'lodash/reverse';
import _sortBy from 'lodash/sortBy';

import { ChartLarge } from './ChartLarge';
import { ChartSmall } from './ChartSmall';
import { EmptyChartMessage } from '../..';
import { calculateTrackSplits } from '../../helpers';

interface DataAdapterResult {
    mappedData: TrackSplits[];
    didFilterSmallValues: boolean;
}

/**
 * Convert the tracks array to a list of track splits, listed in descending order by total earnings.
 *
 * Additionally, we filter out any tracks with total earnings less than minValue and
 * set a flag if any filtering took place.
 */
function dataAdapter(data: ProTrack[], minValue: number): DataAdapterResult {
    const trackSplitsList = [] as TrackSplits[];
    let didFilterSmallValues = false;

    data.forEach((track: ProTrack): void => {
        const trackSplits = calculateTrackSplits(track);

        if (
            trackSplits.total_earnings === null ||
            trackSplits.total_earnings < minValue
        ) {
            didFilterSmallValues = true;
        } else {
            trackSplitsList.push(trackSplits);
        }
    });

    const mappedData = _reverse(_sortBy(trackSplitsList, 'total_earnings'));

    return { mappedData, didFilterSmallValues };
}

interface Props {
    minValue: number;
    tracks: ProTrack[];
    size?: 'large' | 'small';
}

export const EarningsSplitsPerTrack = ({
    minValue,
    tracks,
    size = 'large',
}: Props): JSX.Element => {
    const mappedTrackSplits = useMemo(() => {
        const { mappedData } = dataAdapter(tracks, minValue);

        return mappedData;
    }, [tracks]);

    const chartHasData = mappedTrackSplits.length > 0;

    if (!chartHasData) {
        return <EmptyChartMessage />;
    }

    if (size === 'small') {
        return <ChartSmall trackSplits={mappedTrackSplits} />;
    }

    return <ChartLarge trackSplits={mappedTrackSplits} />;
};
